import type { Artefato, ArtefatoDiff, DiffAnterior, TipoArtefato } from '@/data/types';

/**
 * Diff do manifesto entre duas versões.
 *
 * Na API real quem calcula é o servidor (`diff_anterior` do `BuildDetalhe`); a
 * fonte demo usa `calcularDiff` para produzir o mesmo formato a partir das
 * fixtures. A tela só consome `indexarDiff`, que transforma o diff numa
 * consulta por linha do manifesto.
 *
 * A identidade de um artefato é `artefato + tipo`: o mesmo nome pode existir
 * como `exe` e como `zip` no Nexus, e são coisas diferentes.
 */

export function chaveArtefato(item: { artefato: string; tipo: TipoArtefato }): string {
  return `${item.artefato}:${item.tipo}`;
}

function ordenar<T extends { artefato: string }>(itens: T[]): T[] {
  return [...itens].sort((a, b) => a.artefato.localeCompare(b.artefato, 'pt-BR', { sensitivity: 'base' }));
}

export function calcularDiff(atual: Artefato[], anterior: Artefato[] | null, versaoAnterior: string | null): DiffAnterior {
  if (!anterior) return { versao_anterior: versaoAnterior, alterados: [], adicionados: [], removidos: [] };

  const antes = new Map(anterior.map((item) => [chaveArtefato(item), item]));
  const depois = new Map(atual.map((item) => [chaveArtefato(item), item]));

  const alterados: DiffAnterior['alterados'] = [];
  const adicionados: ArtefatoDiff[] = [];
  for (const [chave, item] of depois) {
    const velho = antes.get(chave);
    if (!velho) adicionados.push({ artefato: item.artefato, tipo: item.tipo, versao: item.versao });
    else if (velho.versao !== item.versao)
      alterados.push({ artefato: item.artefato, tipo: item.tipo, de: velho.versao, para: item.versao });
  }
  const removidos: ArtefatoDiff[] = [];
  for (const [chave, item] of antes) {
    if (!depois.has(chave)) removidos.push({ artefato: item.artefato, tipo: item.tipo, versao: item.versao });
  }

  return {
    versao_anterior: versaoAnterior,
    alterados: ordenar(alterados),
    adicionados: ordenar(adicionados),
    removidos: ordenar(removidos),
  };
}

export type MudancaLinha = { tipo: 'alterado'; de: string } | { tipo: 'adicionado' } | null;

export interface DiffIndexado {
  /** Mudança da linha do manifesto atual (`null` = igual à anterior). */
  mudanca: (item: { artefato: string; tipo: TipoArtefato }) => MudancaLinha;
  totalMudancas: number;
  semAnterior: boolean;
}

export function indexarDiff(diff: DiffAnterior): DiffIndexado {
  const alterados = new Map(diff.alterados.map((item) => [chaveArtefato(item), item.de]));
  const adicionados = new Set(diff.adicionados.map(chaveArtefato));
  return {
    mudanca: (item) => {
      const chave = chaveArtefato(item);
      const de = alterados.get(chave);
      if (de !== undefined) return { tipo: 'alterado', de };
      if (adicionados.has(chave)) return { tipo: 'adicionado' };
      return null;
    },
    totalMudancas: diff.alterados.length + diff.adicionados.length + diff.removidos.length,
    semAnterior: diff.versao_anterior === null,
  };
}

/** Frase-resumo do diff, para o cabeçalho do manifesto. */
export function resumirDiff(diff: DiffAnterior): string {
  if (diff.versao_anterior === null) return 'Primeira versão deste branch — nada para comparar.';
  const partes: string[] = [];
  if (diff.alterados.length) partes.push(`${diff.alterados.length} ${diff.alterados.length === 1 ? 'alterado' : 'alterados'}`);
  if (diff.adicionados.length) partes.push(`${diff.adicionados.length} ${diff.adicionados.length === 1 ? 'novo' : 'novos'}`);
  if (diff.removidos.length) partes.push(`${diff.removidos.length} ${diff.removidos.length === 1 ? 'removido' : 'removidos'}`);
  if (partes.length === 0) return `Manifesto idêntico ao de ${diff.versao_anterior}.`;
  return `Comparado a ${diff.versao_anterior}: ${partes.join(', ')}.`;
}
